/**
 * FolderSelect.tsx
 *
 * The folder control for every folder-backed library under Marketing: email
 * templates, SMS templates and the photo library.
 *
 * It replaces the original rows of folder pills. Pills were fine for the handful
 * of folders we expected; accounts now run dozens, and the row wrapped into
 * several lines that pushed the content grid off screen. So the picker is a
 * compact dropdown, and folder management — which used to hide behind a
 * right-click context menu on a pill — moved into an explicit "Edit Folders"
 * modal beside it.
 *
 * Every scope shares the picker and all three are editable. Photo folders used
 * to be excluded — they were one flat set shared by every tenant, so a rename
 * here would have reorganised every other account's library. They are now
 * scoped: the nine locked system folders and any master-wide folders belong to
 * the master account and are managed in the corporate dashboard, while folders
 * created here are private to this account. The manager renders the first two
 * kinds read-only and only offers rename/delete on rows this account owns.
 */
import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, FolderCog, Pencil, Plus, Search, Trash2, X } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { FilterSelect } from '@/components/ui/FilterSelect';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import { cn } from '@/lib/utils';
import * as marketingSvc from '@/services/marketing.service';
import type { LibraryFolder } from '@/services/marketing.service';

// ─── Scope wiring ─────────────────────────────────────────────────────────────

export type FolderScope =
  | 'email'
  | 'sms'
  | 'photos'
  | 'videos';

/** Every scope is manageable now; the alias is kept so scopeConfig stays typed. */
type ManagedScope = FolderScope;

const scopeConfig = {
  email: {
    foldersKey: ['marketing', 'email-template-folders'] as const,
    contentsKey: ['marketing', 'email-templates'] as const,
    noun: 'template',
    list: marketingSvc.getEmailTemplateFolders,
    create: marketingSvc.createEmailTemplateFolder,
    rename: marketingSvc.renameEmailTemplateFolder,
    remove: marketingSvc.deleteEmailTemplateFolder,
  },
  sms: {
    foldersKey: ['marketing', 'sms-template-folders'] as const,
    contentsKey: ['marketing', 'sms-templates'] as const,
    noun: 'template',
    list: marketingSvc.getSmsTemplateFolders,
    create: marketingSvc.createSmsTemplateFolder,
    rename: marketingSvc.renameSmsTemplateFolder,
    remove: marketingSvc.deleteSmsTemplateFolder,
  },
  photos: {
    foldersKey: ['marketing', 'photo-folders'] as const,
    contentsKey: ['marketing', 'photos'] as const,
    noun: 'photo',
    list: marketingSvc.getPhotoFolders,
    create: marketingSvc.createPhotoFolder,
    rename: marketingSvc.renamePhotoFolder,
    remove: marketingSvc.deletePhotoFolder,
  },
  videos: {
    foldersKey: ['marketing', 'video-folders'] as const,
    contentsKey: ['marketing', 'videos'] as const,
    noun: 'video',
    list: marketingSvc.getVideoFolders,
    create: marketingSvc.createVideoFolder,
    rename: marketingSvc.renameVideoFolder,
    remove: marketingSvc.deleteVideoFolder,
  },
} satisfies Record<ManagedScope, unknown>;

/**
 * Whether a folder can be renamed or deleted from Prospera.
 *
 * Three reasons a row is read-only:
 *  - a null id: the built-in "general" template folder, which has no row of its own;
 *  - isSystem: a locked folder addressed by name in code — the nine photo
 *    folders entity pickers resolve, or the video library's built-ins;
 *  - scope 'master': a folder shared across the master account, owned by the
 *    corporate dashboard. Renaming it here would change it for every sibling.
 */
function folderLockReason(f: LibraryFolder): string | null {
  if (f.id == null) return 'The default folder cannot be changed';
  if (f.isSystem) return 'Required folder — cannot be renamed or deleted';
  if (f.scope === 'master') return 'Shared across the master account — managed in the corporate dashboard';
  return null;
}

/**
 * Surface the API's own message (duplicate/reserved folder name, 404, …).
 *
 * Two shapes in play: the template endpoints send `{error: {message}}`, the
 * photo ones `{error: "…"}`. Both are worth showing — they carry the reason a
 * folder name was rejected.
 */
function apiErrorMessage(err: unknown, fallback: string): string {
  const res = (err as {
    response?: { data?: { error?: { message?: string } | string; message?: string } };
  })?.response?.data;
  if (typeof res?.error === 'string') return res.error || fallback;
  return res?.error?.message ?? res?.message ?? fallback;
}

const titleCase = (name: string) => name.charAt(0).toUpperCase() + name.slice(1);

/** Folders past this count get a filter box in the manager — the reason this rewrite exists. */
const SEARCH_THRESHOLD = 8;

// ─── Selector ─────────────────────────────────────────────────────────────────

export interface FolderSelectProps {
  scope: FolderScope;
  /** Folder rows with the counts the page wants shown. */
  folders: { id: number | null; name: string; count: number }[];
  /** Selected folder name; '' means every folder. */
  activeFolder: string;
  onSelect: (folder: string) => void;
  /** Count shown against "All Folders". */
  totalCount: number;
}

export function FolderSelect({
  scope,
  folders,
  activeFolder,
  onSelect,
  totalCount,
}: FolderSelectProps) {
  const [managerOpen, setManagerOpen] = useState(false);

  const options = useMemo(
    () => [
      { label: 'All Folders', value: '', count: totalCount },
      ...folders.map((f) => ({ label: titleCase(f.name), value: f.name, count: f.count })),
    ],
    [folders, totalCount],
  );

  return (
    <div className="flex items-center gap-2">
      <FilterSelect
        options={options}
        value={activeFolder}
        onChange={onSelect}
        label="Filter by folder"
        className="w-52"
      />
      <Button
        variant="secondary"
        size="sm"
        leftIcon={<FolderCog className="h-4 w-4" />}
        onClick={() => setManagerOpen(true)}
        title="Add, rename or delete folders"
      >
        Edit Folders
      </Button>

      <FolderManagerModal
        scope={scope}
        isOpen={managerOpen}
        onClose={() => setManagerOpen(false)}
        activeFolder={activeFolder}
        onActiveFolderRenamed={onSelect}
        onActiveFolderDeleted={() => onSelect('')}
      />
    </div>
  );
}

// ─── Manager modal ────────────────────────────────────────────────────────────

interface FolderManagerModalProps {
  scope: ManagedScope;
  isOpen: boolean;
  onClose: () => void;
  /** So a rename/delete of the folder currently being viewed can follow through. */
  activeFolder: string;
  onActiveFolderRenamed: (newName: string) => void;
  onActiveFolderDeleted: () => void;
}

function FolderManagerModal({
  scope,
  isOpen,
  onClose,
  activeFolder,
  onActiveFolderRenamed,
  onActiveFolderDeleted,
}: FolderManagerModalProps) {
  const cfg = scopeConfig[scope];
  const toast = useToast();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [editing, setEditing] = useState<{ id: number; name: string } | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<LibraryFolder | null>(null);

  // Reset the transient bits every time the modal opens, so it never reopens
  // mid-rename or with a stale filter hiding folders.
  useEffect(() => {
    if (isOpen) {
      setSearch('');
      setNewName('');
      setEditing(null);
      setDeleteTarget(null);
    }
  }, [isOpen]);

  // Same key the page uses, so this shares its cache rather than refetching.
  const { data: folders, isLoading } = useQuery({
    queryKey: cfg.foldersKey,
    queryFn: () => cfg.list(),
    enabled: isOpen,
  });

  const invalidateFolders = () => qc.invalidateQueries({ queryKey: cfg.foldersKey });
  const invalidateContents = () => qc.invalidateQueries({ queryKey: cfg.contentsKey });

  const createMut = useMutation({
    mutationFn: (name: string) => cfg.create(name),
    onSuccess: () => {
      invalidateFolders();
      setNewName('');
      toast.success('Folder created');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to create folder')),
  });

  const renameMut = useMutation({
    mutationFn: ({ id, name }: { id: number; name: string; previousName: string }) =>
      cfg.rename(id, name),
    // Contents carry the folder name, not its id, so the list has to be refetched
    // alongside the folders. If the renamed folder is the one on screen, the page
    // filter follows it — otherwise the grid would go empty behind the modal.
    onSuccess: (_res, vars) => {
      invalidateFolders();
      invalidateContents();
      if (activeFolder === vars.previousName) onActiveFolderRenamed(vars.name);
      setEditing(null);
      toast.success('Folder renamed');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to rename folder')),
  });

  // Keyed on the whole folder, not just the id: ConfirmDialog clears `deleteTarget`
  // as soon as the promise settles, so the name has to travel with the mutation.
  const deleteMut = useMutation({
    mutationFn: (f: LibraryFolder) => cfg.remove(f.id!),
    onSuccess: (_res, f) => {
      invalidateFolders();
      invalidateContents();
      if (activeFolder === f.name) onActiveFolderDeleted();
      toast.success('Folder deleted');
    },
    onError: (e) => toast.error(apiErrorMessage(e, 'Failed to delete folder')),
  });

  const rows = folders ?? [];
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((f) => f.name.toLowerCase().includes(q));
  }, [rows, search]);

  const submitNew = () => {
    const name = newName.trim();
    if (name) createMut.mutate(name);
  };

  const submitRename = () => {
    if (!editing) return;
    const name = editing.name.trim();
    const previousName = rows.find((f) => f.id === editing.id)?.name ?? '';
    if (name) renameMut.mutate({ id: editing.id, name, previousName });
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title="Edit Folders"
        size="md"
        // A stray Escape while renaming would throw away what was typed, and while
        // the delete confirm is up it would dismiss both dialogs at once — both
        // Escape handlers sit on `document`.
        closeOnEscape={!editing && !deleteTarget}
        footer={
          <Button variant="secondary" onClick={onClose}>
            Done
          </Button>
        }
      >
        <div className="space-y-4">
          {/* Add */}
          <div className="flex items-end gap-2">
            <Input
              label="New folder"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter folder name..."
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  submitNew();
                }
              }}
            />
            <Button
              leftIcon={<Plus className="h-4 w-4" />}
              disabled={!newName.trim()}
              loading={createMut.isPending}
              onClick={submitNew}
            >
              Add
            </Button>
          </div>

          {/* Filter — only earns its space once the list is long */}
          {rows.length > SEARCH_THRESHOLD && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${rows.length} folders...`}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-lg text-sm outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          )}

          {/* List */}
          <div className="border border-slate-200 rounded-lg divide-y divide-slate-100 max-h-80 overflow-y-auto">
            {isLoading ? (
              <div className="flex justify-center py-10">
                <Spinner />
              </div>
            ) : filtered.length === 0 ? (
              <div className="py-10 text-center text-sm text-slate-400">
                {search ? 'No folders match that search' : 'No folders yet'}
              </div>
            ) : (
              filtered.map((f) => {
                const isEditing = editing?.id != null && f.id === editing.id;
                const lockReason = folderLockReason(f);
                const locked = lockReason !== null;
                return (
                  <div
                    key={f.name}
                    className={cn(
                      'flex items-center gap-2 px-3 py-2',
                      isEditing ? 'bg-blue-50/50' : 'hover:bg-slate-50',
                    )}
                  >
                    {isEditing ? (
                      <>
                        <Input
                          value={editing.name}
                          autoFocus
                          onChange={(e) => setEditing({ id: editing.id, name: e.target.value })}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              submitRename();
                            }
                            if (e.key === 'Escape') {
                              e.preventDefault();
                              e.stopPropagation();
                              setEditing(null);
                            }
                          }}
                        />
                        <Button
                          size="sm"
                          iconOnly
                          leftIcon={<Check className="h-4 w-4" />}
                          loading={renameMut.isPending}
                          disabled={!editing.name.trim()}
                          onClick={submitRename}
                          title="Save"
                          aria-label="Save folder name"
                        />
                        <Button
                          size="sm"
                          iconOnly
                          variant="ghost"
                          leftIcon={<X className="h-4 w-4" />}
                          onClick={() => setEditing(null)}
                          title="Cancel"
                          aria-label="Cancel rename"
                        />
                      </>
                    ) : (
                      <>
                        <span className="flex-1 min-w-0 truncate text-sm text-slate-700">
                          {titleCase(f.name)}
                          {f.isSystem ? (
                            <span className="ml-2 text-xs text-slate-400">required</span>
                          ) : f.scope === 'master' ? (
                            <span className="ml-2 text-xs text-slate-400">shared</span>
                          ) : locked ? (
                            <span className="ml-2 text-xs text-slate-400">default</span>
                          ) : null}
                        </span>
                        <span className="text-xs font-semibold text-slate-500 tabular-nums">
                          {f.count} {cfg.noun}
                          {f.count === 1 ? '' : 's'}
                        </span>
                        {/* Fixed-width slot (two 32px icon buttons + gap) so the
                            counts stay in a column whether or not a row has
                            actions. Locked rows render no controls at all —
                            the badge next to the name says why. */}
                        <span
                          className="flex items-center gap-2 w-[72px] justify-end shrink-0"
                          title={lockReason ?? undefined}
                        >
                          {!locked && (
                            <>
                              <Button
                                size="sm"
                                iconOnly
                                variant="ghost"
                                leftIcon={<Pencil className="h-3.5 w-3.5" />}
                                onClick={() => setEditing({ id: f.id!, name: f.name })}
                                title="Rename"
                                aria-label={`Rename ${f.name}`}
                              />
                              <Button
                                size="sm"
                                iconOnly
                                variant="ghost"
                                leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                                className="text-red-600 hover:bg-red-50"
                                onClick={() => setDeleteTarget(f)}
                                title="Delete"
                                aria-label={`Delete ${f.name}`}
                              />
                            </>
                          )}
                        </span>
                      </>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <p className="text-xs text-slate-500">
            Deleting a folder keeps its {cfg.noun}s — they move to General.
            {scope === 'photos' &&
              ' Folders you add here are private to this account; required and shared folders come from the corporate dashboard.'}
          </p>
        </div>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete Folder"
        message={
          deleteTarget
            ? `Delete "${titleCase(deleteTarget.name)}"? Its ${deleteTarget.count} ${cfg.noun}${
                deleteTarget.count === 1 ? '' : 's'
              } will be moved to General.`
            : ''
        }
        confirmLabel="Delete"
        variant="danger"
        // ConfirmDialog owns the spinner, so it needs the promise — and its own
        // close runs in a `finally`, so a rejection has to be swallowed here.
        onConfirm={() =>
          deleteTarget?.id != null
            ? deleteMut.mutateAsync(deleteTarget).catch(() => undefined)
            : undefined
        }
        onClose={() => setDeleteTarget(null)}
      />
    </>
  );
}
